import type {
  BuyerSequenceSelectionMode,
  BuyerSequenceStepDto,
  BuyerSequenceStepKey,
} from "@/api/buyerSequence";
import { Card } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { useDateFormatter } from "@/utils/datetime";

/** The hint on a step that talks about one submitted listing (G5). */
export const REQUIRES_LISTING_HINT = "Select an associated listing.";

/**
 * The seven steps, in the server's rail order (G5).
 *
 * <p>🔴 NO TIMING, NO DUE DATES, NO SUGGESTIONS. The tab is standalone by
 * ruling (G1): it never decides what is due, so the rail never says "due in 3
 * days" or highlights a "next" step. The only fact a row carries beyond its
 * name is whether that step has been LOGGED from this tab, and when.</p>
 *
 * <p>A step that talks about one specific submitted listing is disabled until a
 * listing is selected, and says so in VISIBLE text — a disabled button whose
 * only explanation is a `title` tooltip explains itself to nobody on a touch
 * screen and to nobody using a keyboard.</p>
 */
export function StepRail({
  steps,
  selected,
  selectionMode,
  onSelect,
}: {
  steps: BuyerSequenceStepDto[];
  selected: BuyerSequenceStepKey;
  selectionMode: BuyerSequenceSelectionMode;
  onSelect: (step: BuyerSequenceStepKey) => void;
}) {
  const { date } = useDateFormatter();

  return (
    <Card className="p-space-2">
      <nav aria-label="Outreach steps">
        <ol className="m-0 grid list-none gap-space-1 p-0">
          {steps.map((s) => {
            const disabled = s.requiresListing && selectionMode !== "listing";
            const isSelected = s.key === selected;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => onSelect(s.key)}
                  disabled={disabled}
                  aria-current={isSelected ? "step" : undefined}
                  data-testid={`outreach-step-${s.key}`}
                  className={cn(
                    "flex w-full flex-col items-start gap-space-1 rounded-md px-space-3 py-space-2 text-left",
                    "transition-colors duration-fast ease-standard",
                    "focus-visible:outline-none focus-visible:shadow-goldRing",
                    isSelected ? "bg-gold-tint" : "hover:bg-white-warm",
                    "disabled:cursor-not-allowed disabled:hover:bg-transparent",
                  )}
                >
                  <span className="flex w-full items-center justify-between gap-space-2">
                    <span
                      className={cn(
                        "text-small",
                        isSelected ? "font-semibold text-onyx" : "text-onyx",
                        disabled && "text-gray-mid",
                      )}
                    >
                      {s.name}
                    </span>
                    {s.lastSentAtUtc && (
                      <span
                        className="shrink-0 text-caption text-status-success"
                        data-testid={`outreach-step-${s.key}-sent`}
                      >
                        Sent {date(s.lastSentAtUtc, { year: undefined })} ✓
                      </span>
                    )}
                  </span>
                  {disabled && (
                    <span className="text-caption text-gray-mid">{REQUIRES_LISTING_HINT}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </Card>
  );
}
